import type { WeaponAudioProfile, WeaponId } from "../content/schemas";
import { WEAPONS } from "../content/weapons";
import type { AudioEvent } from "../simulation/snapshot";
import { spatialMix, type SpatialListener } from "./audio-spatial";

export type AudioCombatContext = {
  affixes: readonly string[];
};

export type AffixStingerProfile = {
  gain: number;
  playbackRate: number;
  tones: { freq: number; duration: number; delay?: number; type?: OscillatorType; gain?: number }[];
  noise?: { frequency: number; gain: number; duration: number };
};

function weaponAudioProfile(weaponId: WeaponId): WeaponAudioProfile {
  return WEAPONS[weaponId]?.audioProfile ?? "kinetic";
}

/**
 * Sample-first industrial sci-fi SFX + combat music bus.
 * Prefers short WAV/OGG under public/audio/; layered noise/tones only as fallback.
 */

const MAX_ONE_SHOTS = 12;
const MASTER_DEFAULT = 0.48;
const SFX_DEFAULT = 1;
/** Default music bus level (40%): audible bed without drowning SFX. */
const MUSIC_DEFAULT = 0.4;
const MUSIC_DUCK = 0.35;

type BedVoice = {
  filter: BiquadFilterNode;
  gain: GainNode;
  sources: AudioNode[];
};

export class AudioBus {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private sampleCache = new Map<string, AudioBuffer | null>();
  private samplesStarted = false;

  private master = MASTER_DEFAULT;
  private sfxVolume = SFX_DEFAULT;
  private musicVolume = MUSIC_DEFAULT;
  private muted = false;
  private musicEnabled = true;
  private activeOneShots = 0;

  private thrustBed: BedVoice | null = null;
  private approachBed: BedVoice | null = null;
  private thrustLevel = 0;
  private approachLevel = 0;
  private duckUntil = 0;

  private musicSource: AudioBufferSourceNode | null = null;
  private musicBuffer: AudioBuffer | null = null;
  private musicStarted = false;
  private musicLoading = false;

  private gestureBound = false;

  private combatAffixes: readonly string[] = [];

  public setCombatContext(ctx: AudioCombatContext): void {
    this.combatAffixes = ctx.affixes;
  }

  private hasAffix(id: string): boolean {
    return this.combatAffixes.includes(id);
  }

  public setMuted(muted: boolean): void {
    if (this.muted === muted) return;
    this.muted = muted;
    this.applyGains();
  }

  public isMuted(): boolean {
    return this.muted;
  }

  public setMasterVolume(volume: number): void {
    this.master = Math.max(0, Math.min(1, volume));
    this.applyGains();
  }

  public setSfxVolume(volume: number): void {
    const next = Math.max(0, Math.min(1, volume));
    if (this.sfxVolume === next) return;
    this.sfxVolume = next;
    this.applyGains();
  }

  public setMusicVolume(volume: number): void {
    const next = Math.max(0, Math.min(1, volume));
    if (this.musicVolume === next) return;
    this.musicVolume = next;
    this.applyGains();
  }

  public setMusicEnabled(enabled: boolean): void {
    if (this.musicEnabled === enabled) return;
    this.musicEnabled = enabled;
    if (!enabled) {
      this.stopMusic();
    } else if (this.ctx?.state === "running") {
      void this.ensureMusic();
    }
    this.applyGains();
  }

  public isMusicEnabled(): boolean {
    return this.musicEnabled;
  }

  /** Bind once — resumes AudioContext on first pointer/key/touch gesture. */
  public bindGestureUnlock(target?: EventTarget): void {
    if (this.gestureBound) return;
    const el = target ?? (typeof document !== "undefined" ? document : null);
    if (!el) return;
    this.gestureBound = true;
    const unlock = () => {
      void this.resume();
    };
    el.addEventListener("pointerdown", unlock, { once: true, capture: true });
    el.addEventListener("keydown", unlock, { once: true, capture: true });
    el.addEventListener("touchstart", unlock, { once: true, capture: true });
  }

  public async resume(): Promise<void> {
    const ctx = this.ensure();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {
        /* autoplay policies vary */
      }
    }
    if (ctx.state === "running") {
      this.ensureBeds();
      void this.ensureMusic();
    }
    this.prefetchSamples();
  }

  public handle(events: readonly AudioEvent[], listener?: SpatialListener): void {
    if (this.muted) return;
    const ctx = this.ensure();
    if (!ctx) return;
    if (ctx.state === "suspended") void this.resume();

    const listen: SpatialListener = listener ?? { x: 0, y: 0, facing: 0 };
    let thrust: number | null = null;
    let approach: number | null = null;
    let ducked = false;

    for (const event of events) {
      switch (event.type) {
        case "thrust":
          thrust = Math.max(thrust ?? 0, event.intensity);
          break;
        case "approach":
          approach = Math.max(approach ?? 0, event.intensity);
          break;
        case "boost":
          this.playBoost(ctx);
          thrust = Math.max(thrust ?? 0, 0.9);
          ducked = true;
          break;
        case "enemy_boost":
          this.playEnemyBoost(ctx, listen, event);
          break;
        case "weapon_fire":
          this.playWeaponFire(ctx, event.weaponId, listen, event);
          this.duckThrust(ctx, 0.08);
          ducked = true;
          break;
        case "enemy_fire":
          this.playEnemyFire(ctx, listen, event);
          break;
        case "shield_hit":
          this.playShieldHit(ctx, listen, event);
          ducked = true;
          break;
        case "shield_break":
          this.playShieldBreak(ctx, listen, event);
          ducked = true;
          break;
        case "hull_hit":
          this.playHullHit(ctx, listen, event);
          ducked = true;
          break;
        case "enemy_death":
          this.playEnemyDeath(ctx, listen, event);
          ducked = true;
          break;
        case "pickup":
          this.playPickup(ctx, event);
          break;
        case "extract_tick":
          this.playExtractTick(ctx);
          break;
        case "extract_complete":
          this.playExtractComplete(ctx);
          ducked = true;
          break;
        case "death":
          this.playPlayerDeath(ctx);
          ducked = true;
          break;
        case "assist_lock":
          this.playAssistLock(ctx);
          break;
        case "launch":
          this.playLaunch(ctx);
          ducked = true;
          break;
        case "telegraph_warning":
          this.playTelegraph(ctx, listen, event);
          break;
        case "elite_spawn":
          this.playEliteSpawn(ctx, listen, event);
          ducked = true;
          break;
        case "boss_arena":
          this.playBossArena(ctx, listen, event);
          ducked = true;
          break;
        default:
          break;
      }
    }

    if (ducked) this.duckMusic(ctx, 0.22);

    if (thrust !== null) this.thrustLevel = thrust;
    else this.thrustLevel *= 0.86;

    if (approach !== null) this.approachLevel = approach;
    else this.approachLevel *= 0.9;

    this.tickBeds(ctx);
  }

  /** Short confirm chirp when a kill extends a combo chain. */
  public playKillConfirm(chainCount: number): void {
    if (this.muted) return;
    const ctx = this.ensure();
    if (!ctx || !this.canVoice()) return;
    const pitch = Math.min(920, 420 + chainCount * 28);
    this.toneBurst(ctx, {
      type: "triangle",
      freq: pitch,
      duration: 0.05,
      gain: 0.07,
      filterFreq: pitch * 2.2,
      pan: 0,
    });
  }

  /** Soft UI tick when chain score feed entries land. */
  public playScoreTick(): void {
    if (this.muted) return;
    const ctx = this.ensure();
    if (!ctx || !this.canVoice()) return;
    this.toneBurst(ctx, {
      type: "sine",
      freq: 880,
      duration: 0.035,
      gain: 0.045,
      filterFreq: 2400,
      pan: 0,
    });
  }

  private ensure(): AudioContext | null {
    if (typeof AudioContext === "undefined") return null;
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.sfxGain = this.ctx.createGain();
      this.musicGain = this.ctx.createGain();
      this.compressor = this.ctx.createDynamicsCompressor();
      this.compressor.threshold.value = -20;
      this.compressor.knee.value = 14;
      this.compressor.ratio.value = 3.2;
      this.compressor.attack.value = 0.004;
      this.compressor.release.value = 0.22;
      this.sfxGain.connect(this.compressor);
      this.compressor.connect(this.masterGain);
      this.musicGain.connect(this.masterGain);
      this.masterGain.connect(this.ctx.destination);
      this.applyGains();
      this.noiseBuffer = this.makeNoiseBuffer(this.ctx, 2);
    }
    return this.ctx;
  }

  private applyGains(): void {
    if (!this.masterGain || !this.sfxGain || !this.musicGain) return;
    this.masterGain.gain.value = this.muted ? 0 : this.master;
    this.sfxGain.gain.value = this.sfxVolume;
    const musicOn = this.musicEnabled && !this.muted;
    this.musicGain.gain.value = musicOn ? this.musicVolume : 0;
  }

  private ensureBeds(): void {
    const ctx = this.ctx;
    if (!ctx || !this.sfxGain || this.thrustBed) return;

    this.thrustBed = this.createNoiseBed(ctx, {
      type: "bandpass",
      frequency: 170,
      q: 0.85,
    });
    this.approachBed = this.createNoiseBed(ctx, {
      type: "lowpass",
      frequency: 85,
      q: 0.65,
    });
  }

  private createNoiseBed(
    ctx: AudioContext,
    opts: { type: BiquadFilterType; frequency: number; q: number },
  ): BedVoice {
    const filter = ctx.createBiquadFilter();
    filter.type = opts.type;
    filter.frequency.value = opts.frequency;
    filter.Q.value = opts.q;

    const gain = ctx.createGain();
    gain.gain.value = 0.0001;

    const input = ctx.createGain();
    input.gain.value = 1;

    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer;
    noise.loop = true;
    noise.connect(input);

    const rumble = ctx.createOscillator();
    rumble.type = "sine";
    rumble.frequency.value = opts.frequency * 0.32;
    const rumbleGain = ctx.createGain();
    rumbleGain.gain.value = 0.28;
    rumble.connect(rumbleGain);
    rumbleGain.connect(input);

    input.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain!);

    noise.start();
    rumble.start();

    return { filter, gain, sources: [noise, rumble] };
  }

  private tickBeds(ctx: AudioContext): void {
    this.ensureBeds();
    if (!this.thrustBed || !this.approachBed) return;

    const now = ctx.currentTime;
    const ducked = now < this.duckUntil ? 0.4 : 1;

    const thrustTarget = Math.max(0.0001, this.thrustLevel * 0.12 * ducked);
    this.thrustBed.gain.gain.setTargetAtTime(thrustTarget, now, 0.05);
    const thrustFreq = 130 + this.thrustLevel * 200;
    this.thrustBed.filter.frequency.setTargetAtTime(thrustFreq, now, 0.08);
    this.thrustBed.filter.Q.setTargetAtTime(0.65 + this.thrustLevel * 1.1, now, 0.08);

    const approachTarget = Math.max(0.0001, this.approachLevel * 0.09);
    this.approachBed.gain.gain.setTargetAtTime(approachTarget, now, 0.12);
    this.approachBed.filter.frequency.setTargetAtTime(65 + this.approachLevel * 45, now, 0.15);
  }

  private duckThrust(ctx: AudioContext, seconds: number): void {
    this.duckUntil = Math.max(this.duckUntil, ctx.currentTime + seconds);
  }

  private duckMusic(ctx: AudioContext, seconds: number): void {
    if (!this.musicGain || !this.musicEnabled || this.muted) return;
    const now = ctx.currentTime;
    const ducked = this.musicVolume * MUSIC_DUCK;
    this.musicGain.gain.cancelScheduledValues(now);
    this.musicGain.gain.setTargetAtTime(ducked, now, 0.04);
    this.musicGain.gain.setTargetAtTime(this.musicVolume, now + seconds, 0.18);
  }

  private async ensureMusic(): Promise<void> {
    if (!this.musicEnabled || this.muted || !this.ctx || !this.musicGain) return;
    if (this.musicStarted && this.musicSource) return;
    if (this.musicLoading) return;

    if (!this.musicBuffer) {
      this.musicLoading = true;
      try {
        const res = await fetch("/audio/music_combat.ogg");
        if (!res.ok) return;
        const raw = await res.arrayBuffer();
        this.musicBuffer = await this.ctx.decodeAudioData(raw.slice(0));
      } catch {
        return;
      } finally {
        this.musicLoading = false;
      }
    }

    if (!this.musicBuffer || !this.musicEnabled || this.muted) return;
    this.stopMusic();
    const src = this.ctx.createBufferSource();
    src.buffer = this.musicBuffer;
    src.loop = true;
    src.connect(this.musicGain);
    try {
      src.start();
      this.musicSource = src;
      this.musicStarted = true;
    } catch {
      this.musicSource = null;
      this.musicStarted = false;
    }
  }

  private stopMusic(): void {
    if (this.musicSource) {
      try {
        this.musicSource.stop();
      } catch {
        /* already stopped */
      }
      try {
        this.musicSource.disconnect();
      } catch {
        /* ignore */
      }
    }
    this.musicSource = null;
    this.musicStarted = false;
  }

  private canVoice(): boolean {
    return this.activeOneShots < MAX_ONE_SHOTS;
  }

  private trackVoice(duration: number): void {
    this.activeOneShots += 1;
    globalThis.setTimeout(
      () => {
        this.activeOneShots = Math.max(0, this.activeOneShots - 1);
      },
      Math.ceil(duration * 1000) + 30,
    );
  }

  private out(): AudioNode {
    return this.sfxGain!;
  }

  private playSampleOr(
    key: string,
    ctx: AudioContext,
    fallback: () => void,
    opts: { gain?: number; pan?: number; playbackRate?: number } = {},
  ): void {
    const buffer = this.sampleCache.get(key);
    if (!buffer) {
      // Kick a load if missing; synth fills the gap this frame.
      if (!this.sampleCache.has(key)) void this.loadSample(key);
      fallback();
      return;
    }
    if (!this.canVoice()) return;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const rate = opts.playbackRate ?? 1;
    src.playbackRate.value = rate;
    const gain = ctx.createGain();
    gain.gain.value = Math.max(0.0001, (opts.gain ?? 0.55) * 0.62);
    const pan = ctx.createStereoPanner();
    pan.pan.value = opts.pan ?? 0;
    src.connect(gain);
    gain.connect(pan);
    pan.connect(this.out());
    src.start();
    this.trackVoice(buffer.duration / rate);
  }

  private prefetchSamples(): void {
    if (this.samplesStarted || !this.ctx) return;
    this.samplesStarted = true;
    const keys = [
      "weapon_kinetic",
      "weapon_arc",
      "weapon_torpedo",
      "enemy_fire",
      "shield_hit",
      "shield_break",
      "hull_hit",
      "enemy_death",
      "elite_death",
      "pickup",
      "telegraph",
      "boost",
      "enemy_boost",
      "launch",
      "death",
      "elite_spawn",
      "extract_complete",
    ];
    for (const key of keys) {
      void this.loadSample(key);
    }
  }

  private async loadSample(key: string): Promise<void> {
    if (!this.ctx || this.sampleCache.has(key)) return;
    // Reserve slot so parallel calls don't double-fetch.
    this.sampleCache.set(key, null);
    try {
      const res = await fetch(`/audio/${key}.wav`);
      if (!res.ok) {
        this.sampleCache.set(key, null);
        return;
      }
      const raw = await res.arrayBuffer();
      const buffer = await this.ctx.decodeAudioData(raw.slice(0));
      this.sampleCache.set(key, buffer);
    } catch {
      this.sampleCache.set(key, null);
    }
  }

  private eventMix(
    listener: SpatialListener,
    event: { x?: number; y?: number },
    localGain = 1,
  ): { gain: number; pan: number } {
    if (event.x === undefined || event.y === undefined) {
      return { gain: localGain, pan: 0 };
    }
    const mix = spatialMix(listener, event.x, event.y);
    return { gain: mix.gain * localGain, pan: mix.pan };
  }

  private playWeaponFire(
    ctx: AudioContext,
    weaponId: WeaponId,
    listener: SpatialListener,
    event: { x?: number; y?: number },
  ): void {
    const mix = this.eventMix(listener, event, 1);
    if (mix.gain < 0.02) return;

    const profile = weaponAudioProfile(weaponId);
    if (profile === "torpedo") {
      this.playSampleOr("weapon_torpedo", ctx, () => this.synthTorpedo(ctx, mix), {
        gain: mix.gain,
        pan: mix.pan,
        playbackRate: 0.95 + Math.random() * 0.1,
      });
      return;
    }
    if (profile === "swarm") {
      this.playSampleOr("weapon_torpedo", ctx, () => this.synthTorpedo(ctx, mix), {
        gain: mix.gain * 0.75,
        pan: mix.pan,
        playbackRate: 1.15 + Math.random() * 0.12,
      });
      return;
    }
    if (profile === "charge") {
      this.playSampleOr("weapon_torpedo", ctx, () => this.synthTorpedo(ctx, mix), {
        gain: mix.gain * 1.1,
        pan: mix.pan,
        playbackRate: 0.72 + Math.random() * 0.08,
      });
      return;
    }
    if (profile === "arc") {
      this.playSampleOr("weapon_arc", ctx, () => this.synthArc(ctx, mix), {
        gain: mix.gain * 0.95,
        pan: mix.pan,
        playbackRate: (this.hasAffix("hot_barrels") ? 1.06 : 0.97) + Math.random() * 0.08,
      });
      this.playAffixFireLayers(ctx, mix);
      return;
    }
    const hotPitch = this.hasAffix("hot_barrels") ? 1.08 : 0.96;
    const coolPitch = this.hasAffix("coolant_loop") ? 1.12 : 1;
    const glassPitch = this.hasAffix("glass_cannon") ? 0.92 : 1;
    this.playSampleOr("weapon_kinetic", ctx, () => this.synthKinetic(ctx, mix), {
      gain: mix.gain * (this.hasAffix("glass_cannon") ? 1.08 : 1),
      pan: mix.pan,
      playbackRate: hotPitch * coolPitch * glassPitch + Math.random() * 0.1,
    });
    this.playAffixFireLayers(ctx, mix);
  }

  private playAffixFireLayers(ctx: AudioContext, mix: { gain: number; pan: number }): void {
    if (this.hasAffix("hot_barrels")) {
      this.toneBurst(ctx, {
        freq: 920 + Math.random() * 80,
        duration: 0.045,
        type: "triangle",
        gain: 0.035 * mix.gain,
        filterFreq: 1800,
        pan: mix.pan,
      });
    }
    if (this.hasAffix("coolant_loop")) {
      this.noiseBurst(ctx, {
        duration: 0.05,
        gain: 0.05 * mix.gain,
        filterType: "bandpass",
        frequency: 1400,
        q: 2.8,
        pan: mix.pan,
        pitchSweep: { from: 1600, to: 2100, duration: 0.045 },
      });
    }
    if (this.hasAffix("glass_cannon")) {
      this.noiseBurst(ctx, {
        duration: 0.07,
        gain: 0.09 * mix.gain,
        filterType: "bandpass",
        frequency: 2800,
        q: 3.5,
        pan: mix.pan,
        pitchSweep: null,
      });
    }
  }

  private playEnemyFire(
    ctx: AudioContext,
    listener: SpatialListener,
    event: { x: number; y: number },
  ): void {
    const mix = this.eventMix(listener, event, 0.8);
    if (mix.gain < 0.03) return;
    this.playSampleOr("enemy_fire", ctx, () => this.synthEnemyBolt(ctx, mix), {
      gain: mix.gain,
      pan: mix.pan,
      playbackRate: 0.88 + Math.random() * 0.22,
    });
  }

  private playShieldHit(
    ctx: AudioContext,
    listener: SpatialListener,
    event: { x?: number; y?: number },
  ): void {
    let localGain = 0.85;
    let playbackRate = 0.95 + Math.random() * 0.1;
    if (this.hasAffix("glass_cannon")) {
      localGain *= 0.72;
      playbackRate *= 0.94;
    } else if (this.hasAffix("hard_shell") || this.hasAffix("shield_weave")) {
      localGain *= 1.12;
      playbackRate *= 1.04;
    }
    const mix = this.eventMix(listener, event, localGain);
    if (mix.gain < 0.02) return;
    this.playSampleOr("shield_hit", ctx, () => this.synthShieldHit(ctx, mix), {
      gain: mix.gain,
      pan: mix.pan,
      playbackRate,
    });
  }

  private playShieldBreak(
    ctx: AudioContext,
    listener: SpatialListener,
    event: { x?: number; y?: number },
  ): void {
    const mix = this.eventMix(listener, event, 1);
    if (mix.gain < 0.02) return;
    this.playSampleOr("shield_break", ctx, () => this.synthShieldBreak(ctx, mix), {
      gain: mix.gain,
      pan: mix.pan,
    });
  }

  private playHullHit(
    ctx: AudioContext,
    listener: SpatialListener,
    event: { x?: number; y?: number },
  ): void {
    const mix = this.eventMix(listener, event, 1);
    if (mix.gain < 0.02) return;
    this.playSampleOr("hull_hit", ctx, () => this.synthHullHit(ctx, mix), {
      gain: mix.gain,
      pan: mix.pan,
      playbackRate: 0.92 + Math.random() * 0.14,
    });
  }

  private playEnemyDeath(
    ctx: AudioContext,
    listener: SpatialListener,
    event: { elite: boolean; x?: number; y?: number },
  ): void {
    const mix = this.eventMix(listener, event, event.elite ? 1.1 : 1);
    if (mix.gain < 0.02) return;
    const key = event.elite ? "elite_death" : "enemy_death";
    this.playSampleOr(key, ctx, () => this.synthExplosion(ctx, mix, event.elite), {
      gain: mix.gain,
      pan: mix.pan,
      playbackRate: event.elite ? 0.88 : 0.97 + Math.random() * 0.08,
    });
  }

  private playTelegraph(
    ctx: AudioContext,
    listener: SpatialListener,
    event: { kind: "turret" | "mine"; x?: number; y?: number },
  ): void {
    const mix = this.eventMix(listener, event, 0.7);
    if (mix.gain < 0.03) return;
    this.playSampleOr("telegraph", ctx, () => this.synthTelegraph(ctx, mix, event.kind), {
      gain: mix.gain,
      pan: mix.pan,
      playbackRate: event.kind === "mine" ? 0.75 : 1.05,
    });
  }

  private playEliteSpawn(
    ctx: AudioContext,
    listener: SpatialListener,
    event: { x?: number; y?: number },
  ): void {
    const mix = this.eventMix(listener, event, 0.9);
    if (mix.gain < 0.03) return;
    this.playSampleOr("elite_spawn", ctx, () => this.synthEliteSpawn(ctx, mix), {
      gain: mix.gain,
      pan: mix.pan,
    });
  }

  private playBossArena(
    ctx: AudioContext,
    listener: SpatialListener,
    event: { phase: "enter" | "clear"; x?: number; y?: number },
  ): void {
    const mix = this.eventMix(listener, event, event.phase === "enter" ? 1.15 : 1);
    if (mix.gain < 0.02) return;
    if (event.phase === "enter") {
      this.playSampleOr("elite_spawn", ctx, () => this.synthEliteSpawn(ctx, mix), {
        gain: mix.gain * 1.15,
        pan: mix.pan,
        playbackRate: 0.82,
      });
      this.toneBurst(ctx, {
        type: "sawtooth",
        freq: 160,
        duration: 0.35,
        gain: 0.11 * mix.gain,
        filterFreq: 520,
        pan: mix.pan,
      });
      this.toneBurst(ctx, {
        type: "triangle",
        freq: 95,
        duration: 0.28,
        gain: 0.08 * mix.gain,
        filterFreq: 280,
        pan: mix.pan,
        delay: 0.08,
      });
      return;
    }
    this.playSampleOr(
      "extract_complete",
      ctx,
      () => {
        this.toneBurst(ctx, {
          type: "triangle",
          freq: 420,
          duration: 0.22,
          gain: 0.12 * mix.gain,
          filterFreq: 1400,
          pan: mix.pan,
        });
        this.toneBurst(ctx, {
          type: "sine",
          freq: 640,
          duration: 0.28,
          gain: 0.08 * mix.gain,
          filterFreq: 1800,
          pan: mix.pan,
        });
      },
      {
        gain: mix.gain,
        pan: mix.pan,
        playbackRate: 1.08,
      },
    );
  }

  private playBoost(ctx: AudioContext): void {
    const upgraded = this.hasAffix("afterburn_trim");
    this.playSampleOr(
      "boost",
      ctx,
      () => {
        if (!this.canVoice()) return;
        this.noiseBurst(ctx, {
          duration: upgraded ? 0.26 : 0.2,
          gain: upgraded ? 0.17 : 0.14,
          filterType: "bandpass",
          frequency: upgraded ? 440 : 380,
          q: upgraded ? 1.35 : 1.2,
          pan: 0,
          pitchSweep: {
            from: upgraded ? 520 : 420,
            to: upgraded ? 140 : 180,
            duration: upgraded ? 0.24 : 0.18,
          },
        });
        this.toneBurst(ctx, {
          freq: upgraded ? 110 : 95,
          duration: upgraded ? 0.2 : 0.16,
          type: "sine",
          gain: upgraded ? 0.09 : 0.07,
          filterFreq: upgraded ? 320 : 280,
        });
      },
      {
        gain: upgraded ? 1 : 0.85,
        pan: 0,
        playbackRate: upgraded ? 1.05 + Math.random() * 0.08 : 0.96 + Math.random() * 0.08,
      },
    );
  }

  private playEnemyBoost(
    ctx: AudioContext,
    listener: SpatialListener,
    event: { x: number; y: number },
  ): void {
    const mix = this.eventMix(listener, event, 0.75);
    if (mix.gain < 0.04) return;
    this.playSampleOr(
      "enemy_boost",
      ctx,
      () => {
        if (!this.canVoice()) return;
        this.noiseBurst(ctx, {
          duration: 0.24,
          gain: 0.11 * mix.gain,
          filterType: "lowpass",
          frequency: 520,
          q: 0.9,
          pan: mix.pan,
          pitchSweep: { from: 280, to: 90, duration: 0.22 },
        });
      },
      {
        gain: mix.gain,
        pan: mix.pan,
        playbackRate: 0.85 + Math.random() * 0.12,
      },
    );
  }

  private playPickup(ctx: AudioContext, event: Extract<AudioEvent, { type: "pickup" }>): void {
    const kind = event.kind ?? "other";
    if (kind === "affix" && event.affixId) {
      const profile = affixStingerProfile(event.affixId);
      if (profile) {
        this.playAffixPickup(ctx, profile);
        return;
      }
    }
    if (kind === "salvage") {
      this.playSampleOr("pickup", ctx, () => this.synthPickup(ctx, 0.75), {
        gain: 0.42,
        pan: 0,
        playbackRate: 0.82 + Math.random() * 0.08,
      });
      return;
    }
    this.playSampleOr("pickup", ctx, () => this.synthPickup(ctx, 1), {
      gain: 0.65,
      pan: 0,
      playbackRate: 0.96 + Math.random() * 0.06,
    });
  }

  private playAffixPickup(ctx: AudioContext, profile: AffixStingerProfile): void {
    this.playSampleOr("pickup", ctx, () => this.synthAffixPickup(ctx, profile), {
      gain: profile.gain,
      pan: 0,
      playbackRate: profile.playbackRate,
    });
  }

  private synthAffixPickup(ctx: AudioContext, profile: AffixStingerProfile): void {
    if (!this.canVoice()) return;
    for (const tone of profile.tones) {
      this.toneBurst(ctx, {
        freq: tone.freq,
        duration: tone.duration,
        type: tone.type ?? "triangle",
        gain: tone.gain ?? 0.055,
        filterFreq: tone.freq * 2.2,
        delay: tone.delay,
      });
    }
    if (profile.noise) {
      this.noiseBurst(ctx, {
        duration: profile.noise.duration,
        gain: profile.noise.gain,
        filterType: "bandpass",
        frequency: profile.noise.frequency,
        q: 2.4,
        pan: 0,
        pitchSweep: null,
      });
    }
  }

  private playExtractTick(ctx: AudioContext): void {
    if (!this.canVoice()) return;
    this.toneBurst(ctx, {
      freq: 400,
      duration: 0.045,
      type: "triangle",
      gain: 0.045,
      filterFreq: 800,
    });
  }

  /** Soft tick when a soft-lock is newly acquired (rate-limited in sim). */
  private playAssistLock(ctx: AudioContext): void {
    if (!this.canVoice()) return;
    this.toneBurst(ctx, {
      freq: 620,
      duration: 0.03,
      type: "triangle",
      gain: 0.038,
      filterFreq: 1400,
    });
    this.toneBurst(ctx, {
      freq: 880,
      duration: 0.022,
      type: "sine",
      gain: 0.028,
      filterFreq: 1800,
      delay: 0.018,
    });
  }

  private playExtractComplete(ctx: AudioContext): void {
    this.playSampleOr("extract_complete", ctx, () => this.synthExtractComplete(ctx), {
      gain: 0.75,
      pan: 0,
    });
  }

  private playPlayerDeath(ctx: AudioContext): void {
    this.playSampleOr(
      "death",
      ctx,
      () => {
        if (!this.canVoice()) return;
        this.synthExplosion(ctx, { gain: 1, pan: 0 }, true);
        this.noiseBurst(ctx, {
          duration: 0.55,
          gain: 0.2,
          filterType: "lowpass",
          frequency: 160,
          q: 0.5,
          pan: 0,
          pitchSweep: null,
        });
      },
      { gain: 1, pan: 0 },
    );
  }

  private playLaunch(ctx: AudioContext): void {
    this.playSampleOr(
      "launch",
      ctx,
      () => {
        if (!this.canVoice()) return;
        this.noiseBurst(ctx, {
          duration: 0.45,
          gain: 0.15,
          filterType: "bandpass",
          frequency: 220,
          q: 0.75,
          pan: 0,
          pitchSweep: { from: 150, to: 380, duration: 0.4 },
        });
        this.toneBurst(ctx, {
          freq: 85,
          duration: 0.35,
          type: "sine",
          gain: 0.07,
          filterFreq: 360,
        });
      },
      { gain: 0.8, pan: 0 },
    );
  }

  // —— Soft synth fallbacks (no harsh square tones) ——

  private synthKinetic(ctx: AudioContext, mix: { gain: number; pan: number }): void {
    if (!this.canVoice()) return;
    this.noiseBurst(ctx, {
      duration: 0.065,
      gain: 0.14 * mix.gain,
      filterType: "bandpass",
      frequency: 1600 + Math.random() * 350,
      q: 2,
      pan: mix.pan,
      pitchSweep: null,
    });
  }

  private synthArc(ctx: AudioContext, mix: { gain: number; pan: number }): void {
    if (!this.canVoice()) return;
    this.noiseBurst(ctx, {
      duration: 0.085,
      gain: 0.12 * mix.gain,
      filterType: "bandpass",
      frequency: 2200,
      q: 3.2,
      pan: mix.pan,
      pitchSweep: { from: 2600, to: 900, duration: 0.075 },
    });
    this.toneBurst(ctx, {
      freq: 580 + Math.random() * 40,
      duration: 0.06,
      type: "triangle",
      gain: 0.03 * mix.gain,
      filterFreq: 1200,
      pan: mix.pan,
    });
  }

  private synthTorpedo(ctx: AudioContext, mix: { gain: number; pan: number }): void {
    if (!this.canVoice()) return;
    this.noiseBurst(ctx, {
      duration: 0.26,
      gain: 0.16 * mix.gain,
      filterType: "lowpass",
      frequency: 380,
      q: 0.75,
      pan: mix.pan,
      pitchSweep: { from: 260, to: 80, duration: 0.24 },
    });
    this.toneBurst(ctx, {
      freq: 68,
      duration: 0.2,
      type: "sine",
      gain: 0.09 * mix.gain,
      filterFreq: 200,
      pan: mix.pan,
    });
  }

  private synthEnemyBolt(ctx: AudioContext, mix: { gain: number; pan: number }): void {
    if (!this.canVoice()) return;
    this.noiseBurst(ctx, {
      duration: 0.075,
      gain: 0.1 * mix.gain,
      filterType: "bandpass",
      frequency: 1000 + Math.random() * 280,
      q: 2.6,
      pan: mix.pan,
      pitchSweep: { from: 1300, to: 550, duration: 0.065 },
    });
  }

  private synthShieldHit(ctx: AudioContext, mix: { gain: number; pan: number }): void {
    if (!this.canVoice()) return;
    this.noiseBurst(ctx, {
      duration: 0.075,
      gain: 0.1 * mix.gain,
      filterType: "bandpass",
      frequency: 2400,
      q: 4,
      pan: mix.pan,
      pitchSweep: null,
    });
    this.toneBurst(ctx, {
      freq: 820,
      duration: 0.055,
      type: "triangle",
      gain: 0.04 * mix.gain,
      filterFreq: 2000,
      pan: mix.pan,
    });
  }

  private synthShieldBreak(ctx: AudioContext, mix: { gain: number; pan: number }): void {
    if (!this.canVoice()) return;
    this.noiseBurst(ctx, {
      duration: 0.2,
      gain: 0.18 * mix.gain,
      filterType: "bandpass",
      frequency: 1600,
      q: 1.1,
      pan: mix.pan,
      pitchSweep: { from: 2000, to: 380, duration: 0.18 },
    });
  }

  private synthHullHit(ctx: AudioContext, mix: { gain: number; pan: number }): void {
    if (!this.canVoice()) return;
    this.noiseBurst(ctx, {
      duration: 0.11,
      gain: 0.18 * mix.gain,
      filterType: "lowpass",
      frequency: 340,
      q: 0.85,
      pan: mix.pan,
      pitchSweep: null,
    });
    this.toneBurst(ctx, {
      freq: 90 + Math.random() * 18,
      duration: 0.09,
      type: "sine",
      gain: 0.08 * mix.gain,
      filterFreq: 280,
      pan: mix.pan,
    });
  }

  private synthExplosion(
    ctx: AudioContext,
    mix: { gain: number; pan: number },
    elite: boolean,
  ): void {
    if (!this.canVoice()) return;
    const dur = elite ? 0.42 : 0.26;
    this.noiseBurst(ctx, {
      duration: dur,
      gain: (elite ? 0.24 : 0.18) * mix.gain,
      filterType: "lowpass",
      frequency: elite ? 240 : 320,
      q: 0.55,
      pan: mix.pan,
      pitchSweep: { from: elite ? 200 : 280, to: 55, duration: dur * 0.9 },
    });
    this.toneBurst(ctx, {
      freq: elite ? 46 : 66,
      duration: dur * 0.65,
      type: "sine",
      gain: (elite ? 0.12 : 0.08) * mix.gain,
      filterFreq: 150,
      pan: mix.pan,
    });
  }

  private synthTelegraph(
    ctx: AudioContext,
    mix: { gain: number; pan: number },
    kind: "turret" | "mine",
  ): void {
    if (!this.canVoice()) return;
    if (kind === "mine") {
      this.noiseBurst(ctx, {
        duration: 0.12,
        gain: 0.085 * mix.gain,
        filterType: "bandpass",
        frequency: 150,
        q: 1.8,
        pan: mix.pan,
        pitchSweep: null,
      });
      return;
    }
    this.noiseBurst(ctx, {
      duration: 0.09,
      gain: 0.08 * mix.gain,
      filterType: "bandpass",
      frequency: 820,
      q: 3,
      pan: mix.pan,
      pitchSweep: { from: 650, to: 1100, duration: 0.08 },
    });
  }

  private synthEliteSpawn(ctx: AudioContext, mix: { gain: number; pan: number }): void {
    if (!this.canVoice()) return;
    this.noiseBurst(ctx, {
      duration: 0.32,
      gain: 0.12 * mix.gain,
      filterType: "lowpass",
      frequency: 190,
      q: 0.75,
      pan: mix.pan,
      pitchSweep: { from: 75, to: 200, duration: 0.28 },
    });
  }

  private synthPickup(ctx: AudioContext, brightness = 1): void {
    if (!this.canVoice()) return;
    this.toneBurst(ctx, {
      freq: 500 * brightness,
      duration: 0.065,
      type: "triangle",
      gain: 0.06 * brightness,
      filterFreq: 1600,
    });
    this.toneBurst(ctx, {
      freq: 740 * brightness,
      duration: 0.08,
      type: "triangle",
      gain: 0.045 * brightness,
      filterFreq: 2200,
      delay: 0.045,
    });
  }

  private synthExtractComplete(ctx: AudioContext): void {
    this.toneBurst(ctx, {
      freq: 250,
      duration: 0.11,
      type: "sine",
      gain: 0.07,
      filterFreq: 700,
    });
    this.toneBurst(ctx, {
      freq: 375,
      duration: 0.13,
      type: "sine",
      gain: 0.06,
      filterFreq: 1100,
      delay: 0.09,
    });
  }

  private noiseBurst(
    ctx: AudioContext,
    opts: {
      duration: number;
      gain: number;
      filterType: BiquadFilterType;
      frequency: number;
      q: number;
      pan: number;
      pitchSweep: { from: number; to: number; duration: number } | null;
    },
  ): void {
    if (!this.noiseBuffer || !this.sfxGain) return;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    const filter = ctx.createBiquadFilter();
    filter.type = opts.filterType;
    filter.Q.value = opts.q;
    if (opts.pitchSweep) {
      filter.frequency.setValueAtTime(opts.pitchSweep.from, ctx.currentTime);
      filter.frequency.exponentialRampToValueAtTime(
        Math.max(40, opts.pitchSweep.to),
        ctx.currentTime + opts.pitchSweep.duration,
      );
    } else {
      filter.frequency.value = opts.frequency;
    }
    const gain = ctx.createGain();
    const peak = Math.max(0.0001, opts.gain);
    gain.gain.setValueAtTime(peak, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + opts.duration);
    const pan = ctx.createStereoPanner();
    pan.pan.value = opts.pan;
    src.connect(filter);
    filter.connect(gain);
    gain.connect(pan);
    pan.connect(this.out());
    src.start();
    src.stop(ctx.currentTime + opts.duration + 0.02);
    this.trackVoice(opts.duration);
  }

  private toneBurst(
    ctx: AudioContext,
    opts: {
      freq: number;
      duration: number;
      type: OscillatorType;
      gain: number;
      filterFreq: number;
      pan?: number;
      delay?: number;
    },
  ): void {
    const t0 = ctx.currentTime + (opts.delay ?? 0);
    const osc = ctx.createOscillator();
    // Never use square — harsh and fatiguing.
    osc.type = opts.type === "square" ? "triangle" : opts.type;
    osc.frequency.value = opts.freq;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = opts.filterFreq;
    const gain = ctx.createGain();
    const peak = Math.max(0.0001, opts.gain);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.duration);
    const pan = ctx.createStereoPanner();
    pan.pan.value = opts.pan ?? 0;
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(pan);
    pan.connect(this.out());
    osc.start(t0);
    osc.stop(t0 + opts.duration + 0.02);
    this.trackVoice(opts.duration + (opts.delay ?? 0));
  }

  private makeNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
    const length = Math.floor(ctx.sampleRate * seconds);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    // Soft pink-ish noise (less ear fatigue than white).
    let b0 = 0;
    let b1 = 0;
    let b2 = 0;
    for (let i = 0; i < length; i += 1) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99765 * b0 + white * 0.099046;
      b1 = 0.963 * b1 + white * 0.2965164;
      b2 = 0.57 * b2 + white * 1.0526913;
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.18;
    }
    return buffer;
  }
}

export function affixStingerProfile(affixId: string): AffixStingerProfile | null {
  switch (affixId) {
    case "hot_barrels":
      return {
        gain: 0.78,
        playbackRate: 1.08,
        tones: [
          { freq: 620, duration: 0.07, delay: 0 },
          { freq: 880, duration: 0.09, delay: 0.05 },
        ],
        noise: { frequency: 1200, gain: 0.05, duration: 0.08 },
      };
    case "coolant_loop":
      return {
        gain: 0.7,
        playbackRate: 0.98,
        tones: [
          { freq: 520, duration: 0.08, type: "sine" },
          { freq: 680, duration: 0.1, delay: 0.04, type: "sine" },
        ],
      };
    case "glass_cannon":
      return {
        gain: 0.82,
        playbackRate: 1.02,
        tones: [{ freq: 780, duration: 0.06 }],
        noise: { frequency: 2200, gain: 0.07, duration: 0.1 },
      };
    case "hard_shell":
      return {
        gain: 0.72,
        playbackRate: 0.94,
        tones: [
          { freq: 380, duration: 0.11, type: "sine" },
          { freq: 520, duration: 0.08, delay: 0.06, type: "sine" },
        ],
      };
    case "shield_weave":
      return {
        gain: 0.74,
        playbackRate: 0.96,
        tones: [
          { freq: 440, duration: 0.1, type: "sine" },
          { freq: 590, duration: 0.08, delay: 0.05, type: "sine" },
        ],
      };
    case "afterburn_trim":
      return {
        gain: 0.76,
        playbackRate: 1.05,
        tones: [{ freq: 560, duration: 0.08 }],
        noise: { frequency: 420, gain: 0.06, duration: 0.14 },
      };
    case "nap_predator":
      return {
        gain: 0.75,
        playbackRate: 0.9,
        tones: [{ freq: 320, duration: 0.12, type: "sine" }],
        noise: { frequency: 140, gain: 0.055, duration: 0.16 },
      };
    default:
      return {
        gain: 0.68,
        playbackRate: 1,
        tones: [
          { freq: 540, duration: 0.07 },
          { freq: 720, duration: 0.08, delay: 0.04 },
        ],
      };
  }
}
